import React, { useRef, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { QUERY_KEYS } from 'consts';
import { CustomAccordionMenu, MultiSelectControlledOption } from "components";
import { downloadScorecard, uploadScorecard } from 'api';
import { EsgAddFile, EsgDownloadIcon, EsgUploadIcon } from 'assets';
import { useNotify, useGetConfigParameter, useSelectedSite } from 'hooks';
import { getFiscalYear } from 'utils';


interface FilterValues {
    selectedFiscalYears: MultiSelectControlledOption[];
    selectedToggleOption: 'INPUT_UNIT' | 'DEFAULT_UNIT';
}

interface UploadDataDropdownButtonProps {
    filterValues: FilterValues;
    selectedPeriod: string;
    disabled: boolean;
}

export const UploadDataDropdownButton = ({ filterValues, selectedPeriod, disabled }: UploadDataDropdownButtonProps) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const queryClient = useQueryClient();
    const notify = useNotify();
    const selectedSite = useSelectedSite();

    const { data: configParameter } = useGetConfigParameter() || { data: undefined };
    const { selectedFiscalYears, selectedToggleOption } = filterValues;

    // Get company months from config parameter
    const companyMonths = useMemo(() => configParameter
        ?.filter((param: any) => param.name.includes("company_month"))
        .map((param: any) => param.configValue) ?? ["Jan", "Dec"]
        , [configParameter]);

    // Convert fiscal years to date ranges
    const dateRanges = useMemo(() => {
        if (!selectedFiscalYears.length || !companyMonths) return [];

        return selectedFiscalYears.map(fiscalYear => {
            const year = parseInt(fiscalYear.value.toString().split(' ')[0], 10);
            const fiscalYearData = getFiscalYear(companyMonths, `${year} Fiscal Year`);

            if (!fiscalYearData) {
                return {
                    startDate: `${year}-01-01`,
                    endDate: `${year}-12-31`,
                    fiscalYear: year
                };
            }

            return {
                startDate: fiscalYearData.fromDate,
                endDate: fiscalYearData.endDate,
                fiscalYear: year
            };
        });
    }, [selectedFiscalYears, companyMonths]);

    const { mutateAsync: downloadScorecardData, isLoading: isDownloadingTemplate } = useMutation({
        mutationFn: downloadScorecard,
        onSuccess: async (response: Blob) => {
            try {
                const fileName = `scorecard_${selectedSite?.name || 'data'}.xlsx`;

                const blob = new Blob([response], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
                const url = URL.createObjectURL(blob);

                const link = document.createElement("a");
                link.href = url;
                link.download = fileName;
                link.click();

                URL.revokeObjectURL(url);
                notify.success('Scorecard downloaded successfully.');
            } catch (error) {
                notify.error('Failed to process the downloaded file.');
            }
        },
        onError: (error: AxiosError) => {
            notify.error(typeof error?.response?.data === 'string' ? error.response?.data : 'Failed to download scorecard.');
        },
    });

    const { mutateAsync: uploadScorecardData, isLoading: isUploading } = useMutation({
        mutationFn: uploadScorecard,
        onSuccess: () => {
            notify.success('Scorecard uploaded successfully.');
            queryClient.invalidateQueries([QUERY_KEYS.SCORECARD_DATA]);
        },
        onError: (error: AxiosError) => {
            notify.error(typeof error.response?.data === 'string' ? error.response?.data : 'Failed to upload scorecard.');
        },
    });

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
                notify.error('Please select an Excel file (.xlsx or .xls).');
                return;
            }


            uploadScorecardData({ file, siteId: selectedSite?.id ?? 0 });
            // eslint-disable-next-line no-param-reassign
            event.target.value = '';
        }
    };

    const onUploadTemplateClick = () => {
        if (!selectedSite?.id) {
            notify.error('Please select a site first.');
            return;
        }
        fileInputRef.current?.click();
    };

    const onDownloadTemplateClick = () => {

        if (!selectedSite?.id) {
            notify.error('Please select a site first.');
            return;
        }

        if (!selectedFiscalYears.length) {
            notify.error('Please select at least one fiscal year.');
            return;
        }

        downloadScorecardData({
            siteId: selectedSite.id,
            dateRanges,
            timeframe: selectedPeriod.toLowerCase(),
            unit: selectedToggleOption
        });
    }

    return <><CustomAccordionMenu
        title="Upload Data via Excel"
        initialIcon={<EsgAddFile />}
        disabled={disabled}
        sx={{ width: '230px', height: '36px', '& .MuiPaper-root.MuiAccordion-root': { borderRadius: '8px' } }}
        buttons={[
            {
                label: (isDownloadingTemplate ? 'Downloading...' : 'Download Excel Template'),
                icon: <EsgDownloadIcon />,
                disabled: isDownloadingTemplate || !selectedFiscalYears.length,
                onClick: onDownloadTemplateClick,

            },
            {
                label: (isUploading ? 'Uploading...' : 'Upload Filled Template'),
                icon: <EsgUploadIcon />,
                disabled: isUploading,
                onClick: onUploadTemplateClick,
            },
        ]}
    />

        <input
            type="file"
            accept=".xlsx,.xls"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileUpload}
        /></>
}